import React, { useMemo } from 'react';
import { Box, Button, Card, CardContent, InputAdornment, Stack } from '@mui/material';
import { observer } from 'mobx-react-lite';
import CommonInput from '../../../../component/form/common-input.component';
import TableComponent, { HeadCell } from '../../../../component/table/table.component';
import i18n from '../../../../i18n';
import { formatEUR } from '../../../../constants/tool.fn';
import { ErrorMessageBox } from '../../data-analysis.page';
import DishSalesState, { DishAnalysisType, DishColumnKey } from '../state/dish-sales-state';
import { MenuSalesRow } from '../state/dish-data-shared.types';
import { ReactComponent as SearchIcon } from '../../../../static/images/svg/ic_round-search.svg';
import { ReactComponent as SelectAllIcon } from '../../../../static/images/svg/fluent_select-all-on-16-regular.svg';
import { ReactComponent as CompareIcon } from '../../../../static/images/svg/material-symbols-light_chart-data-outline-rounded.svg';

/** 组件属性。 */
interface DishSalesTabPageProps {
    /** 菜品销售分析状态。 */
    store: DishSalesState;
    /** 打开对比弹窗。 */
    onOpenCompareDialog: () => void;
}

/** 菜品销售分析维度选项。 */
const DISH_ANALYSIS_TYPE_OPTIONS = [
    { value: 'menu', label: 'business_analysis_page_text_menu_sales_analysis' },
    { value: 'category', label: 'business_analysis_page_text_menu_category_sales_analysis' },
    { value: 'dish', label: 'business_analysis_page_text_menu_dish_sales_analysis' },
];

/** 顶部下拉的统一样式。 */
const SELECT_INPUT_SX = {
    '& .base-input': {
        height: 56,
        width: '100%',
    },
    '& .MuiFormControl-root': {
        height: 56,
        width: '100%',
    },
    '& .MuiInputBase-root': {
        height: 56,
        width: '100%',
        pr: '16px',
    },
    '& .MuiOutlinedInput-root': {
        height: 56,
        minHeight: 56,
        width: '100%',
        borderRadius: '16px',
        boxShadow: '0px 0px 4px var(--shadow-50)',
        bgcolor: 'var(--fill-neutral-100)',
        '& .MuiSelect-select': {
            minHeight: '24px',
            py: '15px',
            pl: '16px',
            pr: '8px',
            boxSizing: 'border-box',
            color: 'var(--text-primary-300)',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--stroke-primary-300)',
        },
        '&.Mui-disabled': {
            bgcolor: 'var(--fill-neutral-100)',
            opacity: 1,
            boxShadow: '0px 0px 4px var(--shadow-50)',
        },
        '& .MuiSelect-select.Mui-disabled': {
            WebkitTextFillColor: 'var(--text-primary-300)',
            opacity: 0.56,
        },
    },
};

/**
 * 顶部操作按钮统一样式（与 Figma 3929:29622 默认态、3929:29625 悬停态一致）。
 * 默认：浅底、深色正文、图标为主题色；悬停：主题浅底 + 主题描边 + 主题色字与图标。
 */
const CONTROL_BUTTON_SX = {
    height: 56,
    minHeight: 56,
    borderRadius: '16px',
    px: '16px',
    py: 0,
    boxShadow: '0px 0px 4px var(--shadow-50)',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    /** 与稿面正文一致：20px / 24px / Medium，走主题 typography.body1 */
    typography: 'body1',
    boxSizing: 'border-box',
    border: '1px solid transparent',
    bgcolor: 'var(--fill-neutral-100)',
    color: 'var(--text-dark)',
    // 图标区固定 40×40，与稿面一致；默认/悬停同一尺寸，避免 MUI 或 SVG 在态切换时视觉大小不一致
    '& .MuiButton-startIcon': {
        mr: '8px',
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 0,
    },
    '& .MuiButton-startIcon svg': {
        width: 40,
        height: 40,
        flexShrink: 0,
        display: 'block',
        color: 'var(--text-primary-300)',
    },
    '&:hover:not(.Mui-disabled)': {
        bgcolor: 'var(--fill-primary-200)',
        borderColor: 'var(--stroke-primary-300)',
        color: 'var(--text-primary-300)',
    },
    '&.Mui-disabled': {
        bgcolor: 'var(--fill-neutral-100)',
        color: 'var(--text-dark)',
        borderColor: 'transparent',
        opacity: 0.48,
    },
    '&.Mui-disabled .MuiButton-startIcon svg': {
        color: 'var(--text-primary-300)',
    },
    '&.Mui-disabled:hover': {
        bgcolor: 'var(--fill-neutral-100)',
        borderColor: 'transparent',
        color: 'var(--text-dark)',
    },
};

/**
 * 菜品销售分析页签。
 */
const DishSalesTabPage = observer(({ store, onOpenCompareDialog }: DishSalesTabPageProps) => {
    /**
     * 读取指定列的前端排序箭头状态。
     * @param key - 表头列 id
     * @returns 0 未排序 / 1 降序 / 2 升序
     */
    const getSortStatus = (key: string) => {
        /** 当前排序配置。 */
        const sort = store.sortKeyValue;
        if (key === sort?.key) {
            return sort.value === 'ASC' ? 2 : 1;
        }
        return 0;
    };

    /**
     * 表头点击切换排序（无排序 → 降序 → 升序 → 无排序）。
     * @param headCell - 被点击的表头
     */
    const handleSort = (headCell: HeadCell) => {
        /** 下一档排序方向。 */
        let sortStatus: 'ASC' | 'DESC' | '' = '';
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC';
                break;
            case 1:
                sortStatus = 'ASC';
                break;
            case 2:
                sortStatus = '';
                break;
            default:
                sortStatus = '';
                break;
        }
        store.setSortValue({
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined,
        });
    };

    /** 全量表头定义。 */
    const headCellList = useMemo<HeadCell[]>(() => [
        {
            id: 'menu_name',
            label: i18n.t('business_analysis_page_text_menu_name'),
            minWidth: '180px',
            sortStatus: getSortStatus('menu_name'),
            renderFunction: (row) => row?.menu_name ?? '--',
        },
        {
            id: 'category_name',
            label: i18n.t('business_analysis_page_text_menu_category'),
            minWidth: '180px',
            sortStatus: getSortStatus('category_name'),
            renderFunction: (row) => row?.category_name ?? '--',
        },
        {
            id: 'item_name1',
            label: i18n.t('business_analysis_page_dishes_name_one'),
            minWidth: '200px',
            sortStatus: getSortStatus('item_name1'),
            renderFunction: (row) => row?.item_name1 ?? '--',
        },
        {
            id: 'item_name2',
            label: i18n.t('business_analysis_page_dishes_name_two'),
            minWidth: '200px',
            sortStatus: getSortStatus('item_name2'),
            renderFunction: (row) => row?.item_name2 ?? '--',
        },
        {
            id: 'total_quantity',
            label: i18n.t('business_analysis_page_text_sales_quantity'),
            minWidth: '140px',
            sortStatus: getSortStatus('total_quantity'),
            renderFunction: (row) => row?.total_quantity ?? 0,
        },
        {
            id: 'total_amount',
            label: i18n.t('business_analysis_page_text_sales_total'),
            minWidth: '160px',
            sortStatus: getSortStatus('total_amount'),
            renderFunction: (row) => formatEUR(Number(row?.total_amount ?? 0)),
        },
        {
            id: 'return_rate',
            label: i18n.t('business_analysis_page_text_return_rate'),
            minWidth: '120px',
            sortStatus: getSortStatus('return_rate'),
            renderFunction: (row) => row?.return_rate != null ? `${row.return_rate}%` : '--',
        },
        {
            id: 'sales_ratio',
            label: i18n.t('business_analysis_page_text_sales_ratio'),
            minWidth: '120px',
            sortStatus: getSortStatus('sales_ratio'),
            renderFunction: (row) => row?.sales_ratio != null ? `${row.sales_ratio}%` : '--',
        },
    ], [store.sortKeyValue]);

    /** 当前实际展示的列。 */
    const visibleHeadCellList = useMemo(
        () => headCellList.filter((headCell) => store.visibleColumnKeys.includes(headCell.id as DishColumnKey)),
        [headCellList, store.visibleColumnKeys]
    );
    /** 搜索框输入样式。 */
    const searchInputProps = useMemo(() => ({
        endAdornment: (
            <InputAdornment position="end" sx={{ mr: 0 }}>
                <SearchIcon style={{ width: 24, height: 24, color: 'var(--text-primary-300)' }} />
            </InputAdornment>
        ),
    }), []);

    if (store.errorMessage) {
        return <ErrorMessageBox errorMessage={store.errorMessage} height={360} />;
    }

    return (
        <Stack spacing={3} sx={{ minHeight: 'calc(100vh - 260px)' }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto', overflowY: 'visible', px: '4px', py: '4px' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', flexShrink: 0 }}>
                    <Box sx={{ width: 224, height: 56, flexShrink: 0 }}>
                        <CommonInput
                            inputType="SELECT"
                            select
                            size="small"
                            items={DISH_ANALYSIS_TYPE_OPTIONS}
                            value={store.analysisType}
                            onChange={(event) => store.setAnalysisType(event.target.value as DishAnalysisType)}
                            sx={SELECT_INPUT_SX}
                            fullWidth
                        />
                    </Box>
                    {store.analysisType !== 'menu' ? (
                        <Box sx={{ width: 192, height: 56, flexShrink: 0 }}>
                            <CommonInput
                                inputType="SELECT"
                                select
                                size="small"
                                items={store.mainGroups}
                                value={store.mainGroupId}
                                onChange={(event) => store.setMainGroupId(event.target.value as string)}
                                sx={SELECT_INPUT_SX}
                                fullWidth
                            />
                        </Box>
                    ) : null}
                    {store.analysisType === 'dish' ? (
                        <Box sx={{ width: 208, height: 56, flexShrink: 0 }}>
                            <CommonInput
                                inputType="SELECT"
                                select
                                size="small"
                                items={store.secondMenuOptions}
                                value={store.secondMenuId}
                                onChange={(event) => store.setSecondMenuId(event.target.value as string)}
                                sx={SELECT_INPUT_SX}
                                fullWidth
                            />
                        </Box>
                    ) : null}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', alignItems: 'center', flexShrink: 0, ml: 2 }}>
                    <Box sx={{ width: 160, flexShrink: 0 }}>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            placeholder={i18n.t('general_text_search')}
                            value={store.searchKeyword}
                            onChange={(event) => store.setSearchKeyword(event.target.value)}
                            InputProps={searchInputProps}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 56,
                                    minHeight: 56,
                                    borderRadius: '16px',
                                    boxShadow: '0px 0px 4px var(--shadow-50)',
                                    bgcolor: 'var(--fill-neutral-100)',
                                    boxSizing: 'border-box',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'transparent',
                                    },
                                    '& input': {
                                        py: '15px',
                                        px: '16px',
                                        boxSizing: 'border-box',
                                        color: 'var(--text-primary-300)',
                                    },
                                    '& input::placeholder': {
                                        color: 'var(--text-primary-300)',
                                        opacity: 1,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--stroke-primary-300)',
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={() => store.toggleSelectAllFilteredRows()}
                        disabled={store.filteredRows.length === 0}
                        startIcon={<SelectAllIcon />}
                        sx={{
                            ...CONTROL_BUTTON_SX,
                            minWidth: 108,
                            // 已全选时与稿面「悬停态」视觉一致，便于识别当前状态
                            ...(store.isAllFilteredSelected && store.filteredRows.length > 0
                                ? {
                                      bgcolor: 'var(--fill-primary-200)',
                                      borderColor: 'var(--stroke-primary-300)',
                                      color: 'var(--text-primary-300)',
                                      '& .MuiButton-startIcon svg': {
                                          color: 'var(--text-primary-300)',
                                      },
                                  }
                                : {}),
                        }}
                    >
                        {i18n.t('business_analysis_page_button_select_all')}
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={!store.canCompare}
                        onClick={onOpenCompareDialog}
                        startIcon={<CompareIcon />}
                        sx={{
                            ...CONTROL_BUTTON_SX,
                            minWidth: 132,
                        }}
                    >
                        {i18n.t('business_analysis_page_text_data_compare')}
                    </Button>
                </Box>
            </Box>
            <Card
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    borderRadius: '16px',
                    boxShadow: '0px 0px 4px var(--shadow-25)',
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        flex: 1,
                        minHeight: 0,
                        p: 0,
                        '&:last-child': { pb: 0 },
                    }}
                >
                    <Box sx={{ flex: 1, minHeight: 0, p: 3 }}>
                        <TableComponent
                            isPagination
                            allCount={store.filteredRows.length}
                            headCellList={visibleHeadCellList}
                            list={store.filteredRows}
                            showChecked={0}
                            compareSymbol="rowKey"
                            originSelect={store.selectedRows}
                            isTableLoading={store.loading}
                            selectedRowVariant="primary"
                            handleSort={handleSort}
                            changePageCallback={() => store.clearSelection()}
                            onRowClick={(row) => store.toggleRowSelection(row as MenuSalesRow)}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Stack>
    );
});

export default DishSalesTabPage;
